import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';

import env from './utils/env';
import './App.css';
import './AdminDashboard.css';

// Utility function to format large currency values
const formatCurrencyValue = (value: number): { displayValue: number; suffix: string } => {
    if (value >= 1000000000000) { // Trillions
        return { displayValue: Math.round(value / 1000000000000), suffix: 'T' };
    } else if (value >= 1000000000) { // Billions
        return { displayValue: Math.round(value / 1000000000), suffix: 'B' };
    } else if (value >= 1000000) { // Millions
        return { displayValue: Math.round(value / 1000000), suffix: 'M' };
    } else if (value >= 1000) { // Thousands
        return { displayValue: Math.round(value / 1000), suffix: 'K' };
    } else {
        return { displayValue: Math.round(value), suffix: '' };
    }
};

interface DashboardStats {
    users: {
        total: number;
        active: number;
        verified: number;
        byRole: {
            ADMIN: number;
            USER: number;
            FREE: number;
            TRIAL: number;
        };
        recentSignups: number; // Last 7 days
    };
    bonds: {
        total: number;
        recentlyAdded: number; // Last 7 days
        totalVolume: number;
        currencies: string[];
    };
    reports: {
        total: number;
        processed: number;
        failed: number;
        recentlyProcessed: number; // Last 7 days
    };
    searches: {
        totalSearches: number;
        recentSearches: number; // Last 7 days
        searchTargets: number;
        searchResults: number;
    };
    experiments: {
        total: number;
        active: number;
        completed: number;
        recent: number; // Last 7 days
    };
    feedback: {
        total: number;
        open: number;
        resolved: number;
        recent: number; // Last 7 days
        byType: {
            bug: number;
            feature: number;
            general: number;
            improvement: number;
        };
        byPriority: {
            low: number;
            medium: number;
            high: number;
        };
    };
    system: {
        lastUpdated: string;
        totalDocuments: number;
        totalEmbeddings: number;
    };
}

interface TaskStatusData {
    elkInsights: {
        totalTasks: number;
        healthyTasks: number;
        degradedTasks: number;
        failingTasks: number;
        overallSuccessRate: number;
        totalExecutions: number;
        topFailingTasks: string[];
        slowestTasks: string[];
    };
    airtableStats: {
        totalTasks: number;
        activeTasks: number;
        runningTasks: number;
        completedTasks: number;
        failedTasks: number;
        totalProcessedDocuments: number;
    };
    system: {
        elkTasks: {
            status: 'connected' | 'disconnected' | 'error' | 'unknown';
            taskCount?: number;
        };
        airtable: {
            status: 'connected' | 'disconnected' | 'error' | 'unknown';
            totalTasks?: number;
        };
    };
}

const AdminDashboard: React.FC = () => {
    const { token, isAuthenticated, authLoaded } = useAuth();
    const navigate = useNavigate();
    const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
    const [taskStatusData, setTaskStatusData] = useState<TaskStatusData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const apiUrl = env.apiUrl;

    const fetchDashboardStats = async () => {
        if (!token) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.get(`${apiUrl}/admin/dashboard-stats`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            setDashboardStats(response.data);
        } catch (err: any) {
            console.error('Error fetching dashboard stats:', err);
            setError(err.response?.data?.error || 'Failed to fetch dashboard statistics');
        } finally {
            setLoading(false);
        }
    };

    const fetchTaskStatus = async () => {
        if (!token) return;
        
        try {
            const response = await axios.get(`${apiUrl}/admin/task-status`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setTaskStatusData(response.data);
        } catch (err: any) {
            console.error('Error fetching task status:', err);
        }
    };

    useEffect(() => {
        if (authLoaded && isAuthenticated && token) {
            fetchDashboardStats();
            fetchTaskStatus();
        }
    }, [authLoaded, isAuthenticated, token]);

    const StatCard: React.FC<{ 
        title: string; 
        icon: string; 
        color: string; 
        children: React.ReactNode;
        subtitle?: string;
    }> = ({ title, icon, color, children, subtitle }) => (
        <div className="dashboard-stat-card">
            <div className="dashboard-stat-header" style={{ borderLeftColor: color }}>
                <div className="dashboard-stat-title">
                    <i className={`fas ${icon}`} style={{ color }}></i>
                    <div>
                        <h3>{title}</h3>
                        {subtitle && <p className="dashboard-stat-subtitle">{subtitle}</p>}
                    </div>
                </div>
            </div>
            <div className="dashboard-stat-content">
                {children}
            </div>
        </div>
    );

    const MetricItem: React.FC<{ 
        label: string; 
        value: number | string; 
        trend?: number;
        suffix?: string;
        prefix?: string;
    }> = ({ label, value, trend, suffix = '', prefix = '' }) => (
        <div className="dashboard-metric-item">
            <div className="dashboard-metric-main">
                <span className="dashboard-metric-label">{label}</span>
                <span className="dashboard-metric-value">
                    {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
                </span>
            </div>
            {trend !== undefined && (
                <div className={`dashboard-metric-trend ${trend >= 0 ? 'positive' : 'negative'}`}>
                    <i className={`fas ${trend >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                    {Math.abs(trend)}%
                </div>
            )}
        </div>
    );

    const ProgressMetric: React.FC<{ 
        label: string; 
        value: number; 
        total: number; 
        color?: string 
    }> = ({ label, value, total, color = '#2196F3' }) => {
        const percentage = total > 0 ? (value / total) * 100 : 0;
        return (
            <div className="dashboard-progress-metric">
                <div className="dashboard-progress-header">
                    <span className="dashboard-progress-label">{label}</span>
                    <span className="dashboard-progress-values">
                        {value.toLocaleString()} / {total.toLocaleString()}
                    </span>
                </div>
                <div className="dashboard-progress-bar">
                    <div 
                        className="dashboard-progress-fill" 
                        style={{ width: `${percentage}%`, backgroundColor: color }}
                    ></div>
                </div>
                <div className="dashboard-progress-percentage">{percentage.toFixed(1)}%</div>
            </div>
        );
    };

    if (!authLoaded || !isAuthenticated) {
        return (
            <div className="container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading authentication...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container admin-dashboard">
            <div className="page-header">
                <h1>
                    <i className="fas fa-tachometer-alt"></i>
                    Admin Dashboard
                </h1>
                <p className="page-subtitle">
                    Overview of system metrics and key statistics
                </p>
                {dashboardStats?.system.lastUpdated && (
                    <div className="last-updated">
                        <i className="fas fa-clock"></i>
                        Last updated: {new Date(dashboardStats.system.lastUpdated).toLocaleString()}
                    </div>
                )}
                <button 
                    onClick={fetchDashboardStats} 
                    disabled={loading}
                    className="refresh-button"
                >
                    <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                    Refresh Data
                </button>
            </div>

            {error && (
                <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {error}
                </div>
            )}

            {loading && (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboard statistics...</p>
                </div>
            )}

            {!loading && !error && dashboardStats && (
                <div className="dashboard-grid">

                    {/* Task Monitoring Overview */}
                    {taskStatusData && taskStatusData.elkInsights && taskStatusData.airtableStats && taskStatusData.system ? (
                      <StatCard
                        title="Task Monitoring"
                        icon="fa-chart-line"
                        color="#17a2b8"
                        subtitle="ELK & Airtable task insights"
                      >
                          <div className="dashboard-subsection">
                              <h4>ELK Analytics (7 Days)</h4>
                              <MetricItem label="Total Task Types" value={taskStatusData.elkInsights.totalTasks || 0} />
                              <div className="task-health-overview">
                                  <div className="health-metric healthy">
                                      <span className="health-count">{taskStatusData.elkInsights.healthyTasks || 0}</span>
                                      <span className="health-label">Healthy</span>
                                  </div>
                                  <div className="health-metric degraded">
                                      <span className="health-count">{taskStatusData.elkInsights.degradedTasks || 0}</span>
                                      <span className="health-label">Degraded</span>
                                  </div>
                                  <div className="health-metric failing">
                                      <span className="health-count">{taskStatusData.elkInsights.failingTasks || 0}</span>
                                      <span className="health-label">Failing</span>
                                  </div>
                              </div>
                              <MetricItem
                                label="Success Rate"
                                value={`${(taskStatusData.elkInsights.overallSuccessRate || 0).toFixed(1)}%`}
                              />
                              <MetricItem
                                label="Total Executions"
                                value={(taskStatusData.elkInsights.totalExecutions || 0).toLocaleString()}
                              />

                              {taskStatusData.elkInsights.topFailingTasks && taskStatusData.elkInsights.topFailingTasks.length > 0 && (
                                <div className="problem-alert">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>Top Failing: {taskStatusData.elkInsights.topFailingTasks.slice(0, 2).join(', ')}</span>
                                </div>
                              )}
                          </div>

                          <div className="dashboard-subsection">
                              <h4>Airtable Tasks (Current)</h4>
                              <MetricItem label="Total Tasks" value={taskStatusData.airtableStats.totalTasks || 0} />
                              <MetricItem label="Currently Running" value={taskStatusData.airtableStats.runningTasks || 0} />
                              <MetricItem label="Recently Completed" value={taskStatusData.airtableStats.completedTasks || 0} />
                              <MetricItem
                                label="Documents Processed"
                                value={(taskStatusData.airtableStats.totalProcessedDocuments || 0).toLocaleString()}
                              />
                          </div>

                          <div className="dashboard-subsection">
                              <h4>System Status</h4>
                              <div className="system-status-grid">
                                  <div className={`status-item status-${taskStatusData.system.elkTasks?.status || 'unknown'}`}>
                                      <i className="fas fa-chart-line"></i>
                                      <span>ELK Tasks</span>
                                      <span className="status-label">{taskStatusData.system.elkTasks?.status || 'unknown'}</span>
                                      {taskStatusData.system.elkTasks?.taskCount && (
                                        <span className="status-detail">{taskStatusData.system.elkTasks.taskCount} logs</span>
                                      )}
                                  </div>
                                  <div className={`status-item status-${taskStatusData.system.airtable?.status || 'unknown'}`}>
                                      <i className="fas fa-table"></i>
                                      <span>Airtable</span>
                                      <span className="status-label">{taskStatusData.system.airtable?.status || 'unknown'}</span>
                                      {taskStatusData.system.airtable?.totalTasks && (
                                        <span className="status-detail">{taskStatusData.system.airtable.totalTasks} tasks</span>
                                      )}
                                  </div>
                              </div>
                          </div>
                      </StatCard>
                    ) : (
                      <StatCard
                        title="Task Monitoring"
                        icon="fa-chart-line"
                        color="#17a2b8"
                        subtitle="Loading task insights..."
                      >
                          <div className="dashboard-subsection">
                              <div style={{
                                  padding: '20px',
                                  textAlign: 'center',
                                  color: 'var(--text-secondary, #6c757d)',
                                  fontStyle: 'italic'
                              }}>
                                  <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                  Loading task monitoring data...
                              </div>
                          </div>
                      </StatCard>
                    )}

                    {/* Users Overview */}
                    <StatCard 
                        title="Users" 
                        icon="fa-users" 
                        color="#4CAF50"
                        subtitle="User accounts and activity"
                    >
                        <MetricItem label="Total Users" value={dashboardStats.users.total} />
                        <MetricItem label="Active Users" value={dashboardStats.users.active} />
                        <ProgressMetric 
                            label="Verified Users" 
                            value={dashboardStats.users.verified} 
                            total={dashboardStats.users.total}
                            color="#4CAF50"
                        />
                        <MetricItem label="New Signups (7 days)" value={dashboardStats.users.recentSignups} />
                        
                        <div className="dashboard-subsection">
                            <h4>By Role</h4>
                            <MetricItem label="Admins" value={dashboardStats.users.byRole.ADMIN} />
                            <MetricItem label="Pro Users" value={dashboardStats.users.byRole.USER} />
                            <MetricItem label="Free Users" value={dashboardStats.users.byRole.FREE} />
                            <MetricItem label="Trial Users" value={dashboardStats.users.byRole.TRIAL} />
                        </div>
                    </StatCard>

                    {/* Bonds Overview */}
                    <StatCard 
                        title="Bonds" 
                        icon="fa-chart-line" 
                        color="#2196F3"
                        subtitle="Financial instruments data"
                    >
                        <MetricItem label="Total Bonds" value={dashboardStats.bonds.total} />
                        <MetricItem label="Recently Added (7 days)" value={dashboardStats.bonds.recentlyAdded} />
                        {(() => {
                            const formatted = formatCurrencyValue(dashboardStats.bonds.totalVolume);
                            return (
                                <MetricItem 
                                    label="Total Volume" 
                                    value={formatted.displayValue}
                                    suffix={formatted.suffix}
                                    prefix="€"
                                />
                            );
                        })()}
                        <div className="dashboard-subsection">
                            <h4>Currencies</h4>
                            <div className="dashboard-currency-list">
                                {dashboardStats.bonds.currencies.map(currency => (
                                    <span key={currency} className="dashboard-currency-badge">{currency}</span>
                                ))}
                            </div>
                        </div>
                    </StatCard>

                    {/* Reports Overview */}
                    <StatCard 
                        title="Reports" 
                        icon="fa-file-alt" 
                        color="#FF9800"
                        subtitle="Document processing status"
                    >
                        <MetricItem label="Total Reports" value={dashboardStats.reports.total} />
                        <ProgressMetric 
                            label="Processed" 
                            value={dashboardStats.reports.processed} 
                            total={dashboardStats.reports.total}
                            color="#4CAF50"
                        />
                        <MetricItem label="Processing Failures" value={dashboardStats.reports.failed} />
                        <MetricItem label="Recently Processed (7 days)" value={dashboardStats.reports.recentlyProcessed} />
                    </StatCard>

                    {/* Search Activity */}
                    <StatCard 
                        title="Search Activity" 
                        icon="fa-search" 
                        color="#9C27B0"
                        subtitle="Search usage and results"
                    >
                        <MetricItem label="Total Searches" value={dashboardStats.searches.totalSearches} />
                        <MetricItem label="Recent Searches (7 days)" value={dashboardStats.searches.recentSearches} />
                        <MetricItem label="Search Targets" value={dashboardStats.searches.searchTargets} />
                        <MetricItem label="Search Results" value={dashboardStats.searches.searchResults} />
                    </StatCard>

                    {/* Experiments Overview */}
                    <StatCard 
                        title="Experiments" 
                        icon="fa-flask" 
                        color="#f44336"
                        subtitle="AI/ML experiment tracking"
                    >
                        <MetricItem label="Total Experiments" value={dashboardStats.experiments.total} />
                        <MetricItem label="Active" value={dashboardStats.experiments.active} />
                        <MetricItem label="Completed" value={dashboardStats.experiments.completed} />
                        <MetricItem label="Recent (7 days)" value={dashboardStats.experiments.recent} />
                    </StatCard>

                    {/* Feedback Overview */}
                    <StatCard 
                        title="User Feedback" 
                        icon="fa-comments" 
                        color="#17a2b8"
                        subtitle="User feedback and suggestions"
                    >
                        <MetricItem label="Total Feedback" value={dashboardStats.feedback.total} />
                        <MetricItem label="Open Items" value={dashboardStats.feedback.open} />
                        <ProgressMetric 
                            label="Resolved" 
                            value={dashboardStats.feedback.resolved} 
                            total={dashboardStats.feedback.total}
                            color="#28a745"
                        />
                        <MetricItem label="Recent (7 days)" value={dashboardStats.feedback.recent} />
                        
                        <div className="dashboard-subsection">
                            <h4>By Type</h4>
                            <MetricItem label="Bug Reports" value={dashboardStats.feedback.byType.bug} />
                            <MetricItem label="Feature Requests" value={dashboardStats.feedback.byType.feature} />
                            <MetricItem label="General" value={dashboardStats.feedback.byType.general} />
                            <MetricItem label="Improvements" value={dashboardStats.feedback.byType.improvement} />
                        </div>
                        
                        <div className="dashboard-subsection">
                            <h4>By Priority</h4>
                            <MetricItem label="High Priority" value={dashboardStats.feedback.byPriority.high} />
                            <MetricItem label="Medium Priority" value={dashboardStats.feedback.byPriority.medium} />
                            <MetricItem label="Low Priority" value={dashboardStats.feedback.byPriority.low} />
                        </div>
                    </StatCard>



                    {/* System Overview */}
                    <StatCard 
                        title="System Status" 
                        icon="fa-server" 
                        color="#607D8B"
                        subtitle="Overall system health"
                    >
                        <MetricItem label="Total Documents" value={dashboardStats.system.totalDocuments} />
                        <MetricItem label="Total Embeddings" value={dashboardStats.system.totalEmbeddings} />
                        <div className="dashboard-subsection">
                            <h4>Quick Actions</h4>
                            <div className="dashboard-action-buttons">
                                <button 
                                    className="dashboard-action-btn"
                                    onClick={() => navigate('/stats')}
                                >
                                    <i className="fas fa-chart-bar"></i>
                                    View Detailed Stats
                                </button>
                                <button 
                                    className="dashboard-action-btn"
                                    onClick={() => navigate('/task-status')}
                                >
                                    <i className="fas fa-tasks"></i>
                                    Full Task Status
                                </button>
                                <button 
                                    className="dashboard-action-btn"
                                    onClick={() => navigate('/user-management')}
                                >
                                    <i className="fas fa-users-cog"></i>
                                    Manage Users
                                </button>
                                <button 
                                    className="dashboard-action-btn"
                                    onClick={() => navigate('/feedback-management')}
                                >
                                    <i className="fas fa-comments"></i>
                                    Manage Feedback
                                </button>
                            </div>
                        </div>
                    </StatCard>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;